import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AzureAD } from 'react-aad-msal'
import { authProvider } from '../../../services/authProvider'
import store from '../../../store/configureStore'
import { Container, Row, Col, Badge, Button } from 'reactstrap'
import PageContainer from '../../Shared/LayoutComponents/PageContainer'
import PageHeading from '../../Shared/LayoutComponents/PageHeading'
import LoadingSpinner from '../../Shared/LoadingSpinner'
import {
  fetchMyLicenseRequests,
  retryLicenseRequest,
  cancelLicenseRequest,
  approveLicenseRequest,
} from '../../actions/cspLicensesActions'
import { friendlyFailureMessage } from './cspLicensesFriendlyError'
import CSPLicenseDevPanel from './CSPLicenseDevPanel'
import { useCSPLicenseEffectiveRoles } from './devImpersonation'
import { canUseLicenceFeature } from './featureAccess'
import BasketLineRow from './BasketLineRow'
import { REQUEST_STATUS_COLOR } from './requestStatusColor'
import { formatDateTime } from '../../Shared/formatDateTime'

const StatusBadge = ({ status }) => (
  <Badge color={REQUEST_STATUS_COLOR[status] || 'secondary'}>{status}</Badge>
)

const SectionLabel = ({ children }) => (
  <div className="font-weight-bold mb-1 small text-uppercase text-muted">{children}</div>
)

// Plain-English status callout based on where the request is in its
// lifecycle.
const StatusCallout = ({ request, canSelfApprove }) => {
  if (request.status === 'Applied') {
    return (
      <div className="alert alert-success py-2 mb-3 small">
        <strong>Applied.</strong> The change is in. It may take up to
        30 minutes to show up in the customer's Microsoft tenant.
      </div>
    )
  }
  if (request.status === 'Pending') {
    if (canSelfApprove) {
      return (
        <div className="alert alert-warning py-2 mb-3 small">
          <strong>Awaiting approval.</strong> This change is larger than
          your auto-approval limit. You can approve it yourself, or wait
          for someone else to review it.
        </div>
      )
    }
    return (
      <div className="alert alert-warning py-2 mb-3 small">
        <strong>Awaiting approval.</strong> This change is larger than
        your auto-approval limit. Another team member needs to review it
        before it reaches Dicker Data.
      </div>
    )
  }
  if (request.status === 'Failed') {
    const friendly = friendlyFailureMessage(request.lastFailureReason)
    if (!friendly) return null
    return (
      <div className="alert alert-warning py-2 mb-3 small">
        <strong>What happened:</strong> {friendly}
      </div>
    )
  }
  if (request.status === 'Rejected') {
    return (
      <div className="alert alert-dark py-2 mb-3 small">
        <strong>Rejected{request.approverDisplayName ? ` by ${request.approverDisplayName}` : ''}.</strong>
        {request.rejectionReason ? ` ${request.rejectionReason}` : ''}
      </div>
    )
  }
  if (request.status === 'Cancelled') {
    return (
      <div className="alert alert-secondary py-2 mb-3 small">
        Cancelled — no further action.
      </div>
    )
  }
  return null
}

// Diagnostic attempt history. Hidden by default; revealed via the
// "Show details" toggle on the detail panel. Sales Admin and
// engineers needing to correlate with DD's logs see DdTransactionId,
// live observations, response codes, and per-line outcomes here.
const AttemptsPanel = ({ attempts }) => {
  if (!attempts || attempts.length === 0) {
    return <div className="text-muted small">No attempts recorded.</div>
  }
  return (
    <div>
      {attempts.map((a) => (
        <div key={a.id} className="mb-3 p-2 border rounded">
          <div className="small text-muted mb-1">
            <strong>Attempt #{a.attemptNumber}</strong> ({a.attemptKind})
            {' '}&middot; {formatDateTime(a.attemptedAt)}
            {' '}&middot; by {a.attemptedByUpn}
          </div>
          <div className="small text-muted mb-2">
            DD transaction id: <code>{a.ddTransactionId}</code>
          </div>
          {a.failureReason && (
            <div className="alert alert-warning py-1 small mb-2">
              {a.failureReason}
            </div>
          )}
          <table className="table table-sm mb-0">
            <thead>
              <tr>
                <th>Line</th>
                <th className="text-right">Live qty</th>
                <th className="text-right">Sent</th>
                <th>DD response</th>
              </tr>
            </thead>
            <tbody>
              {(a.lineResults || []).map((lr) => (
                <tr key={lr.lineItemId}>
                  <td>{lr.skuDisplayName || lr.lineItemId}</td>
                  <td className="text-right">{lr.liveQuantityAtAttempt}</td>
                  <td className="text-right">{lr.quantitySent}</td>
                  <td>
                    <Badge color={lr.lineSucceeded ? 'success' : 'danger'} className="mr-2">
                      {lr.ddResponseStatus || (lr.lineSucceeded ? '0000' : 'FAILED')}
                    </Badge>
                    <small className="text-muted">{lr.ddResponseMessage}</small>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  )
}

const RequestActions = ({ request, isBusy, dispatch, canSelfApprove }) => {
  if (request.status === 'Failed') {
    // Mirrors the server's two retry blocks (RETRY_BLOCKED_STALE and
    // RETRY_BLOCKED_REMOVED in CSPLicenseRequestService.RetryAsync). The
    // server is the enforcement; this only avoids offering a button whose
    // every press is guaranteed to fail. Keep the two lists in step - a code
    // blocked there but enabled here reads as a broken button.
    const reason = (request.lastFailureReason || '').toUpperCase()
    const retryBlockedMessage = reason.startsWith('STALE_QUANTITY')
      ? 'The seat count has changed since this request was made. Cancel this one and start over with current numbers.'
      : reason.startsWith('SUB_REMOVED_AT_DD')
        ? 'One or more subscriptions no longer exist at Dicker Data, so this request can never be applied. Press Sync on the customer page, then start a new request.'
        : null
    const retryBlocked = retryBlockedMessage !== null
    return (
      <div>
        <Button
          color="primary"
          size="sm"
          className="mr-2"
          disabled={isBusy || retryBlocked}
          onClick={() => dispatch(retryLicenseRequest(request.id))}
          title={retryBlockedMessage || ''}
        >
          Try again
        </Button>
        <Button
          color="outline-secondary"
          size="sm"
          disabled={isBusy}
          onClick={() => dispatch(cancelLicenseRequest(request.id))}
        >
          Cancel
        </Button>
      </div>
    )
  }
  if (request.status === 'Pending') {
    // Self-approval entry point (Sales / Admin only). Labelled distinctly
    // from the peer Approve button so the audit trail is unambiguous.
    // ITManager / SSE requesters see Cancel only and the read-only
    // callout above explains how their request progresses.
    return (
      <div>
        {canSelfApprove && (
          <Button
            color="success"
            size="sm"
            className="mr-2"
            disabled={isBusy}
            onClick={() => {
              if (window.confirm('Approve your own request? This will send the change to Dicker Data immediately.')) {
                dispatch(approveLicenseRequest(request.id))
              }
            }}
          >
            Approve own request
          </Button>
        )}
        <Button
          color="outline-secondary"
          size="sm"
          disabled={isBusy}
          onClick={() => dispatch(cancelLicenseRequest(request.id))}
        >
          Cancel
        </Button>
      </div>
    )
  }
  // Applied / Rejected / Cancelled are fully terminal - no actions.
  return null
}

const RequestDetailPanel = ({ request, canSelfApprove }) => {
  const dispatch = useDispatch()
  const lifecycleRequestId = useSelector((s) => s.cspLicenses.lifecycleRequestId)
  const isBusy = lifecycleRequestId === request.id
  const [showTechnical, setShowTechnical] = useState(false)

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex align-items-start mb-1">
          <h5 className="mb-0 flex-grow-1">
            {request.companyName} <StatusBadge status={request.status} />
          </h5>
        </div>
        <small className="text-muted d-block mb-3">
          Submitted {formatDateTime(request.createdAt)}
          {' '}&middot; CW ticket #{request.cwTicketId}
        </small>

        <StatusCallout request={request} canSelfApprove={canSelfApprove} />

        <RequestActions request={request} isBusy={isBusy} dispatch={dispatch} canSelfApprove={canSelfApprove} />

        <Row className="mt-4">
          <Col>
            <SectionLabel>Requested changes</SectionLabel>
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th className="text-right">Before</th>
                  <th className="text-right">After</th>
                </tr>
              </thead>
              <tbody>
                {(request.lineItems || []).map((li) => (
                  <BasketLineRow key={li.id}
                    skuDisplayName={li.skuDisplayName}
                    stockCode={li.stockCode}
                    stockDescription={li.stockDescription}
                    previousQuantity={li.previousQuantity}
                    requestedQuantity={li.requestedQuantity}
                  />
                ))}
              </tbody>
            </table>
          </Col>
        </Row>

        <div className="mt-3">
          <Button
            color="link"
            size="sm"
            className="p-0 small"
            onClick={() => setShowTechnical((v) => !v)}
          >
            {showTechnical ? 'Hide details' : 'Show details'}
          </Button>
          {showTechnical && (
            <div className="mt-2">
              <SectionLabel>Attempts</SectionLabel>
              <AttemptsPanel attempts={request.attempts} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const CSPLicenseMyRequestsInner = ({ realRoles }) => {
  // Self-approval gate honours the dev override so an engineer
  // signed in as Admin can flip to "SSE" and confirm the Approve own
  // request button correctly hides.
  const roles = useCSPLicenseEffectiveRoles(realRoles)
  const canSelfApprove = roles.includes('Sales') || roles.includes('Admin')

  const dispatch = useDispatch()
  const myRequests = useSelector((s) => s.cspLicenses.myRequests)
  const loading = useSelector((s) => s.cspLicenses.myRequestsLoading)

  const [selectedId, setSelectedId] = useState(null)
  const [statusFilter, setStatusFilter] = useState('All')
  // Client-side pagination - default 10 rows / page. Page resets to 1
  // whenever the filter changes so the admin doesn't end up on page 4
  // of a 2-page filtered list.
  const PAGE_SIZE = 10
  const [page, setPage] = useState(1)

  useEffect(() => {
    dispatch(fetchMyLicenseRequests())
    const hashId = (window.location.hash || '').replace(/^#/, '')
    if (hashId) setSelectedId(hashId)
  }, [dispatch])

  const filtered = useMemo(() => {
    if (statusFilter === 'All') return myRequests
    return myRequests.filter((r) => r.status === statusFilter)
  }, [myRequests, statusFilter])

  // Reset to page 1 when the filter changes or the underlying list
  // shrinks past the current page (e.g. cancel/reject moves a row
  // out of the active filter).
  useEffect(() => {
    setPage(1)
  }, [statusFilter])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const visible = useMemo(
    () => filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filtered, currentPage])

  const selected = useMemo(
    () => myRequests.find((r) => r.id === selectedId) || visible[0] || null,
    [myRequests, selectedId, visible])

  return (
    <PageContainer>
      <Container fluid className="mt-3 mb-4">
        <Row>
          <Col>
            <Link to="/CSPLicenses" className="small">&larr; Back to 365 Licenses</Link>
            <PageHeading>My Licence Change Requests</PageHeading>
          </Col>
        </Row>

        {loading ? (
          <LoadingSpinner />
        ) : myRequests.length === 0 ? (
          <div className="text-muted">You have no licence change requests yet.</div>
        ) : (
          <Row className="mt-3">
            <Col md={5}>
              <div className="d-flex mb-2 align-items-center">
                <strong className="mr-2">Filter:</strong>
                {['All', 'Pending', 'Applied', 'Failed', 'Rejected', 'Cancelled'].map((s) => (
                  <Button
                    key={s}
                    color={statusFilter === s ? 'primary' : 'outline-secondary'}
                    size="sm"
                    className="mr-1"
                    onClick={() => setStatusFilter(s)}
                  >
                    {s}
                  </Button>
                ))}
              </div>
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Submitted</th>
                    <th>Customer</th>
                    <th className="text-right">Lines</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((r) => (
                    <tr
                      key={r.id}
                      onClick={() => setSelectedId(r.id)}
                      style={{ cursor: 'pointer' }}
                      className={selected?.id === r.id ? 'table-active' : ''}
                    >
                      <td><small>{formatDateTime(r.createdAt)}</small></td>
                      <td>{r.companyName}</td>
                      <td className="text-right">{(r.lineItems || []).length}</td>
                      <td><StatusBadge status={r.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtered.length > PAGE_SIZE && (
                <div className="d-flex justify-content-between align-items-center small text-muted">
                  <span>
                    Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}
                  </span>
                  <div>
                    <Button size="sm" color="outline-secondary" className="mr-2"
                      disabled={currentPage <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}>
                      ← Prev
                    </Button>
                    <Button size="sm" color="outline-secondary"
                      disabled={currentPage >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                      Next →
                    </Button>
                  </div>
                </div>
              )}
            </Col>
            <Col md={7}>
              {selected ? <RequestDetailPanel request={selected} canSelfApprove={canSelfApprove} /> : null}
            </Col>
          </Row>
        )}
      </Container>
    </PageContainer>
  )
}

const CSPLicenseMyRequests = () => (
  <AzureAD provider={authProvider} reduxStore={store}>
    {({ accountInfo }) => {
      const realRoles = accountInfo?.account?.idToken?.roles || []
      const allowed = canUseLicenceFeature(realRoles)
      if (!allowed) {
        return (
          <PageContainer>
            <Container fluid className="mt-3">
              <p>You do not have permission to view this page.</p>
            </Container>
          </PageContainer>
        )
      }
      return (
        <>
          <CSPLicenseMyRequestsInner realRoles={realRoles} />
          <CSPLicenseDevPanel />
        </>
      )
    }}
  </AzureAD>
)

export default CSPLicenseMyRequests
